import { useState } from 'react';
import { useApiClient } from '@/lib/api/hooks';
import type { ApiErrorBody, QuoteError } from './_shared';

/**
 * Output shape mirrors the backend's `CalcParseResponseSchema`. Every
 * field optional — only what the LLM recognised comes back. Notes is a
 * short human explainer we surface beneath the textbox.
 */
export type CalcParseResult = {
  sxl?: number;
  xxl?: number;
  xxxl?: number;
  xxxxl?: number;
  xxxxxl?: number;
  frontEnabled?: boolean;
  frontColors?: number;
  backEnabled?: boolean;
  backColors?: number;
  sleeveEnabled?: boolean;
  sleeveColors?: number;
  garmentSource?: 'customer_provides' | 'we_provide' | 'we_provide_by_style';
  blankCostEach?: number;
  garmentColor?: 'white' | 'colored';
  style?: string;
  colorName?: string;
  mode?: 'b2c' | 'b2b';
  discountPct?: number;
  notes?: string;
  wantsGarmentSuggestion?: boolean;
  garmentCategory?: 'tee' | 'hoodie';
};

export type ParseCalcState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; result: CalcParseResult }
  | { status: 'error'; error: QuoteError };

export function useParseCalc() {
  const client = useApiClient();
  const [state, setState] = useState<ParseCalcState>({ status: 'idle' });

  async function parse(text: string): Promise<CalcParseResult | null> {
    setState({ status: 'loading' });
    try {
      const { data, error, response } = await client.POST('/calc/parse', {
        body: { text },
      });
      if (error || !data) {
        const envelope = error as { error?: ApiErrorBody } | undefined;
        const quoteError: QuoteError = {
          status: response.status,
          body: envelope?.error ?? {
            type: 'unknown',
            title: 'Unknown error',
            detail: `HTTP ${response.status}`,
          },
        };
        setState({ status: 'error', error: quoteError });
        return null;
      }
      setState({ status: 'success', result: data.data });
      return data.data;
    } catch (err) {
      const quoteError: QuoteError = {
        status: 0,
        body: {
          type: 'network_error',
          title: 'Could not reach the API',
          detail: err instanceof Error ? err.message : 'Request failed before reaching the server.',
        },
      };
      setState({ status: 'error', error: quoteError });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle' });
  }

  return { state, parse, reset };
}
